import React, { useState, useEffect } from "react";

import GeneralInformation from "./Generalinformation";
import PopupSettings from "./Popupsettings";
import Miscellaneous from "./Miscellaneous";

import { GeneralInformationIcon, MisceIcon, PopUpSettingsIcon } from "../../../components/common/SvgIcon";
import { Toast } from "../../../components/common/Toast";
import MainPageLayout from "../../../components/common/MainPageLayout";

const SettingInfo = ({ setIsLoading, isLoading = false }) => {
  const ProBadge = `${LEGAL_PAGES.assets_url}/images/icons/pro-badge.png`;
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("legal_pages_settings_tab") || "general";
  });

  const [formData, setFormData] = useState({
    site_url: "",
    site_name: "",
    business_niche: "",
    phone_number: "",
    email_address: "",
    street_name: "",
    city: "",
    state: "",
    country_name: "",
    zip_code: "",
    complete_address: "",
    facebook_url: "",
    google_plus_url: "",
    linkedin_url: "",
    twitter_url: "",
  });

  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isProActive = LEGAL_PAGES?.license?.status === "valid";

  const tabs = [
    { id: "general", label: "General Information", icons: <GeneralInformationIcon /> },
    { id: "popup", label: "Popup Settings", badge: <ProBadge />, icons: <PopUpSettingsIcon /> },
    { id: "miscellaneous", label: "Miscellaneous", icons: <MisceIcon /> },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      Toast.error("Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === "popup" && !isProActive) {
      Toast.warning("Please activate PRO license to access Popup Settings");
      return;
    }
    setActiveTab(tabId);
    localStorage.setItem("legal_pages_settings_tab", tabId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (formData.email_address && formData.email_address.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_address)) {
        Toast.error('Please provide a valid email address');
        return;
      }
    }

    setSaving(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": LEGAL_PAGES.nonce,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        Toast.success("Settings saved successfully!");
        if (result.data) {
          setFormData(result.data);
        }
      } else {
        Toast.error("Failed to save settings.");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      Toast.error("An error occurred while saving settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      site_url: "",
      site_name: "",
      business_niche: "",
      phone_number: "",
      email_address: "",
      street_name: "",
      city: "",
      state: "",
      country_name: "",
      zip_code: "",
      complete_address: "",
      facebook_url: "",
      google_plus_url: "",
      linkedin_url: "",
      twitter_url: "",
    });
    Toast.info("Fields reset successfully!");
  };

  const handleResetSavedData = async () => {
    setIsDeleting(true);
    setShowDeleteModal(false);

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/settings/reset`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        Toast.success("Saved data deleted successfully!");

        // Clear UI form fields
        setFormData({
          site_url: "",
          site_name: "",
          business_niche: "",
          phone_number: "",
          email_address: "",
          street_name: "",
          city: "",
          state: "",
          country_name: "",
          zip_code: "",
          complete_address: "",
          facebook_url: "",
          linkedin_url: "",
          twitter_url: "",
        });
      } else {
        Toast.error("Failed to reset saved data.");
      }
    } catch (error) {
      console.error("Reset error:", error);
      Toast.error("Something went wrong.");
    } finally {
      setIsDeleting(false);
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    }
  };


  return (
    
    <MainPageLayout
      title="Settings"
      description="Configure your business information and plugin preferences"
    >
      {/* Tabs */}
      <div className="mt-8">
        <div className="inline-flex rounded-xl bg-[#F4F4F5] p-[4px]">
          {tabs.map((tab) => {
            const isLocked = tab.id === "popup" && !isProActive;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                disabled={isLocked}
                className={[
                  "relative flex cursor-pointer items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium transition",
                  isActive
                    ? "bg-white text-[#17171C] shadow-[0px_1px_2px_0px_#0000000D] ring-1 ring-gray-200"
                    : "text-gray-600 hover:text-gray-900",
                  isLocked ? "cursor-not-allowed opacity-60" : "",
                ].join(" ")}
              >
                {tab.icons}

                <span className="flex items-center gap-1">
                  {tab.label}

                  {isLocked && (
                    <img
                      src={ProBadge}
                      alt="Pro"
                      className="h-4 w-auto"
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {activeTab === "general" && (
        <GeneralInformation
          formData={formData}
          handleInputChange={handleInputChange}
          saving={saving}
          handleSave={handleSave}
          handleReset={handleReset}
          isLoading={isLoading || isDeleting}
          setIsLoading={setIsLoading}
          handleResetSavedData={handleResetSavedData}
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}

      {activeTab === "popup" && isProActive && (
        <PopupSettings
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        />
      )}
      {activeTab === "miscellaneous" && (
        <Miscellaneous 
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}
    </MainPageLayout>
  );
};

export default SettingInfo;